// src/config.js — ClippCut's settings and saved scripts (in this browser).
import { makeStore, clamp, randId } from './core/util'
import { WPM, FS, titleOf } from './core/script'
import { ASPECTS } from './rec/formats'

export const store = makeStore('clippcut:')
export const session = makeStore('clippcut:', 'session')
export const MAX_SCRIPT = 30000
export { WPM, FS }

export const SAMPLE = `# Hook
Most people film the same video five times because they forget their lines. [PAUSE] Here's how I stopped doing that.

# The tip
Write the script first, then read it right under the lens. The words scroll at your pace, so your eyes stay on the camera and you sound like yourself.

# Call to action
Try it on your next video, and follow for more tips like this one.`

/*
 * Two groups of settings, saved together:
 *   the recorder   shape, camera, captions
 *   the prompter   what the shared engine (core/prompter/engine.js) reads
 */
export const DEFAULTS = {
  aspect: '9x16', facing: 'user', hdWanted: true, captions: false, captionStyle: 'pop', countdown: 3, mirrorPreview: false,
  wpm: 140, fontSize: 30, lineHeight: 1.3, margin: 5, font: 'read', look: 'white', align: 'center', guide: 30,
  mirror: false, flipV: false, atEnd: 'stop', prompterHeight: 34, prompterOpacity: 0.62,
  sections: false, zones: true,
  stickers: true, stickerStyle: 'pop', stickerPos: 'center',
}
export function clampSettings(s) {
  if (!ASPECTS.some((a) => a.id === s.aspect)) s.aspect = '9x16'
  if (!['user', 'environment'].includes(s.facing)) s.facing = 'user'
  s.hdWanted = s.hdWanted !== false
  s.captions = !!s.captions
  if (!['pop', 'punch', 'karaoke', 'hero', 'clean'].includes(s.captionStyle)) s.captionStyle = { bold: 'pop', box: 'pop' }[s.captionStyle] || 'pop'   // older ids → today's
  s.countdown = [0, 3, 5].includes(+s.countdown) ? +s.countdown : 3
  s.mirrorPreview = !!s.mirrorPreview
  s.wpm = clamp(Math.round(+s.wpm || 140), WPM.min, WPM.max)
  s.fontSize = clamp(Math.round(+s.fontSize || 30), 16, 72)
  s.lineHeight = clamp(+s.lineHeight || 1.3, 1.1, 1.7)
  s.margin = clamp(Math.round(+s.margin || 5), 2, 20)
  if (!['read', 'serif', 'mono', 'system'].includes(s.font)) s.font = 'read'
  if (!['white', 'amber'].includes(s.look)) s.look = 'white'
  if (!['left', 'center'].includes(s.align)) s.align = 'center'
  s.guide = clamp(Math.round(+s.guide || 30), 10, 70)
  s.mirror = false; s.flipV = false
  if (!['stop', 'loop'].includes(s.atEnd)) s.atEnd = 'stop'
  s.prompterHeight = clamp(Math.round(+s.prompterHeight || 34), 20, 70)
  s.prompterOpacity = clamp(+s.prompterOpacity || 0.62, 0.2, 0.95)
  s.sections = !!s.sections
  s.zones = s.zones !== false
  s.stickers = s.stickers !== false
  if (!['pop', 'bounce', 'slide', 'ring', 'beat'].includes(s.stickerStyle)) s.stickerStyle = 'pop'
  if (!['left', 'center', 'right'].includes(s.stickerPos)) s.stickerPos = 'center'
  return s
}

const subs = new Set()
export const settings = clampSettings({ ...DEFAULTS, ...store.get('settings', {}) })
export function setSettings(patch) {
  const before = { ...settings }
  Object.assign(settings, patch)
  clampSettings(settings)
  store.set('settings', settings)
  subs.forEach((f) => f(before))
}
export const onSettings = (f) => { subs.add(f); return () => subs.delete(f) }

/* Scripts: a short list kept in this browser. */
export const Lib = {
  items: [], currentId: null, subs: new Set(),
  load() {
    let items = store.get('scripts', null)
    if (!Array.isArray(items) || !items.length) items = [{ id: randId(), name: 'Sample: the hook', text: SAMPLE, updated: Date.now() }]
    this.items = items.filter((x) => x && typeof x.text === 'string' && x.id).slice(0, 60)
    if (!this.items.length) this.items = [{ id: randId(), name: 'Untitled script', text: '', updated: Date.now() }]
    this.currentId = store.get('currentScript', this.items[0].id)
    if (!this.get(this.currentId)) this.currentId = this.items[0].id
    this.persist()
  },
  get(id) { return this.items.find((x) => x.id === id) },
  current() { return this.get(this.currentId) || this.items[0] },
  persist() { store.set('scripts', this.items); store.set('currentScript', this.currentId); this.subs.forEach((f) => f()) },
  select(id) { if (this.get(id)) { this.currentId = id; this.persist() } },
  upsert(id, name, text) {
    let it = this.get(id)
    if (!it) { it = { id: id || randId() }; this.items.unshift(it) }
    if (name != null) it.name = String(name).trim()
    if (!it.name) it.name = titleOf(text)
    it.text = String(text).slice(0, MAX_SCRIPT); it.updated = Date.now()
    this.currentId = it.id; this.persist()
    return it
  },
  remove(id) {
    this.items = this.items.filter((x) => x.id !== id)
    if (!this.items.length) this.items = [{ id: randId(), name: 'Untitled script', text: '', updated: Date.now() }]
    if (!this.get(this.currentId)) this.currentId = this.items[0].id
    this.persist()
  },
}
if (typeof window !== 'undefined') Lib.load()
